import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { initEditorBridge } from "@/lib/editorBridge";
import { applyElementOverrides } from "@/lib/applyOverrides";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import SmoothScroll from "@/components/motion/SmoothScroll";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import OfferPopup from "@/components/OfferPopup";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Wishlist from "@/pages/Wishlist";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import TrackOrder from "@/pages/TrackOrder";
import AuthPage from "@/pages/AuthPage";
import AdminLogin from "@/pages/AdminLogin";
import NotFound from "@/pages/NotFound";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Wholesale from "@/pages/Wholesale";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Policy from "@/pages/Policy";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import ProductForm from "@/pages/admin/ProductForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminWebsiteControl from "@/pages/admin/AdminWebsiteControl";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminWebsiteEditor from "@/pages/admin/AdminWebsiteEditor";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTheme from "@/pages/admin/AdminTheme";

function StoreLayout() {
  const { theme, content } = useTheme();
  const [waSettings, setWaSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setWaSettings(r.data)).catch(() => {}); }, []);
  const waDigits = (waSettings?.whatsapp || "").replace(/\D/g, "");
  const waNumber = waDigits.length === 10 ? `91${waDigits}` : waDigits;
  if (theme.maintenance_mode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6] px-4 text-center" data-testid="maintenance-page">
        <p className="font-serif text-2xl font-semibold">HUMAIRA <span className="text-gold">CRAFTS</span></p>
        <p className="text-muted-foreground mt-4 max-w-md text-sm">{content.maintenance_message}</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <OfferPopup />
      <CartDrawer />
      {waNumber && (
        <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Humaira Crafts, I have a question about your collection.")}`}
          target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp"
          className="fixed z-40 bottom-4 left-4 md:bottom-6 md:left-6 w-12 h-12 rounded-full bg-[#111] border border-gold/50 flex items-center justify-center text-gold hover:bg-gold hover:text-[#111] transition-colors duration-300 shadow-xl"
          data-testid="whatsapp-float">
          <MessageCircle strokeWidth={1.5} size={22} />
        </a>
      )}
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // jump instantly (bypass Lenis smoothing) so a new page never opens mid-scroll
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function OverrideApplier() {
  const { settings } = useTheme();
  const location = useLocation();
  useEffect(() => {
    const t = setTimeout(() => applyElementOverrides(settings?.element_overrides), 400);
    return () => clearTimeout(t);
  }, [location.pathname, settings]);
  return null;
}

function App() {
  useEffect(() => { initEditorBridge(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ThemeProvider>
            <SmoothScroll />
            <ScrollToTop />
            <OverrideApplier />
            <Routes>
              <Route element={<StoreLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/category/:slug" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-success/:orderNumber" element={<OrderSuccess />} />
                <Route path="/track-order" element={<TrackOrder />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<AuthPage />} />
                <Route path="/account" element={<Account />} />
                <Route path="/wholesale" element={<Wholesale />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/policies/:type" element={<Policy />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<ProductForm />} />
                <Route path="products/:id/edit" element={<ProductForm />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="orders" element={<AdminOrders />} />
          <Route path="returns" element={<AdminReturns />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="enquiries" element={<AdminEnquiries />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="website-control" element={<AdminWebsiteControl />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="editor" element={<AdminWebsiteEditor />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="theme" element={<AdminTheme />} />
              </Route>
            </Routes>
            <Toaster position="top-center" toastOptions={{ style: { background: "#111", color: "#FAF9F6", border: "1px solid #333", borderRadius: "2px" } }} />
            </ThemeProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
